/**
 * NAVDashboard.js — NAV / Performance
 * ===================================
 * Usability review of 23 October 2026, item 16: *"Remove the header that
 * repeats the Dashboard ... This page should open with what is specific to
 * it: performance and the NAV history."* So the page opens on one line of NAV
 * (the anchor for the date picker), then performance, then the history, and
 * the breakdown and the detail tables below.
 *
 * Three reads, each shown from the last saved copy at once and brought up to
 * date in the background (item 4 — the NAV call takes about five seconds):
 *
 *   /market-data/nav/            the valuation at the chosen date
 *   /market-data/nav/            again at the comparison date (item 20)
 *   /reports/nav-history/        net assets since inception, and the windows
 *
 * The working notes that used to sit under the chart are in ⓘ tips and the
 * Methodology tab (item 5).
 */

import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import FinTable from "../components/FinTable";
import { Badge } from "../components/ui";
import useCachedGet from "../components/useCachedGet";
import { ExportCsvButton, FreshnessBadge, InfoTip, PdfButton } from "../components/PageTools";
import { amount, day, money, pct, quantity, toNumber } from "../components/format";
import { groupKey, rollUpPositions, venueOnRow } from "../components/positionRollup";
import { dealReportPath } from "../components/links";
import PerformanceTiles from "./nav/PerformanceTiles";
import NetAssetsChart from "./nav/NetAssetsChart";
import NavBreakdown from "./nav/NavBreakdown";
import InvestorStatement from "./nav/InvestorStatement";
import Methodology from "./nav/Methodology";
import { compareHoldings, delta, displayTicker, previousMonthEnd, previousYearEnd } from "./nav/navLogic";
import "./NAVDashboard.css";

const whole = (v) => money(v, { whole: true });
const signed = (v) => (v > 0 ? `+${whole(v)}` : whole(v));
const toneClass = (v) => (toNumber(v) > 0 ? "nvp-pos" : toNumber(v) < 0 ? "nvp-neg" : "");
const today = () => new Date().toISOString().slice(0, 10);

const TYPE_BADGE = {
  Equity: "blue",
  "Convertible Note": "amber",
  "Convertible Preferred": "yellow",
  PPLN: "gray",
};

// A past valuation is today's surviving book filtered by trade date (§0.31).
// The payload says so; the page says it in its own words, next to the date.
function PastDateNote({ basis }) {
  if (!basis || basis.is_point_in_time !== false) return null;
  return (
    <InfoTip label="Why a past date is approximate">
      <p>
        A past date is approximate. It shows today’s holdings, kept to those bought on or before that date and priced as
        at it. Anything sold since is missing, and anything sold down since shows what is left of it.
      </p>
    </InfoTip>
  );
}

function Holding({ row, dealIds, asOfDate }) {
  const ticker = displayTicker(row.ticker);
  const title = row.name || ticker || "Unnamed holding";
  const ids = dealIds.get(groupKey(row)) || new Set();
  const dealId = ids.size === 1 ? [...ids][0] : null;
  return (
    <div className="nvp-holding">
      <strong>{title}</strong>
      <span className="nvp-holding-meta">
        {ticker && ticker !== title && <span className="nvp-ticker">{ticker}</span>}
        {row.instrument_type && <Badge text={row.instrument_type} variant={TYPE_BADGE[row.instrument_type] || "gray"} style={{ padding: "0 7px", fontSize: 10, lineHeight: "16px" }} />}
      </span>
      {row.deal_name && (dealId !== null
        ? <Link className="nvp-deal" to={dealReportPath(dealId)}>Deal: {row.deal_name}</Link>
        : <span className="nvp-deal">Deal: {row.deal_name}</span>)}
      {row.held_now === false && <span className="nvp-flag">Not held on {day(asOfDate)}</span>}
    </div>
  );
}

// The basis a holding with no price is carried on, in two or three words. The
// engine's own wording is kept in the cell's title and in the CSV.
export function basisLabel(method) {
  const m = String(method || "");
  if (/principal/i.test(m)) return "Cost less repaid";
  if (/unpriced/i.test(m)) return "Cost, no price";
  if (/cost/i.test(m)) return "At cost";
  return m || "No price";
}

function Price({ row }) {
  if (toNumber(row.last_price) === null) {
    return row.held_now === false ? "—"
      : <span className="nvp-muted nvp-small" title={row.valuation_method || undefined}>{basisLabel(row.valuation_method)}</span>;
  }
  return (
    <div className="nvp-stack">
      <span>{money(row.last_price, { currency: row.currency, decimals: 4 })}</span>
      {row.price_date && <small>{day(row.price_date)}</small>}
    </div>
  );
}

export default function NAVDashboard() {
  const [asOfDate, setAsOfDate] = useState(today);
  const [compareMode, setCompareMode] = useState("month");
  const [customCompare, setCustomCompare] = useState("");
  const [search, setSearch] = useState("");
  const [instrument, setInstrument] = useState("all");
  const [tab, setTab] = useState("positions");
  const [investor, setInvestor] = useState(null);

  const compareDate = compareMode === "month" ? previousMonthEnd(asOfDate)
    : compareMode === "year" ? previousYearEnd(asOfDate)
      : compareMode === "custom" ? customCompare : "";

  const nav = useCachedGet("/market-data/nav/", { params: { date: asOfDate } });
  const history = useCachedGet("/reports/nav-history/", { params: { date: asOfDate } });
  const prior = useCachedGet(compareDate ? "/market-data/nav/" : null, { params: compareDate ? { date: compareDate } : undefined });

  const navData = nav.data;
  const compareData = compareDate ? prior.data : null;
  const summary = navData?.summary || {};

  // The NAV payload is one row per tax lot. A reader wants the instrument, so
  // the lots are rolled up before anything counts, filters or sorts them —
  // the same roll-up the Dashboard's Fund Positions table uses (§0.21).
  const positions = useMemo(() => rollUpPositions(navData?.positions), [navData]);
  const priorPositions = useMemo(() => rollUpPositions(compareData?.positions), [compareData]);
  // A holding links to its deal only when every lot behind it is on one deal;
  // the roll-up keeps the first lot's deal, which would be a guess otherwise.
  const dealIds = useMemo(() => {
    const out = new Map();
    for (const lot of [...(navData?.positions || []), ...(compareData?.positions || [])]) {
      const key = groupKey({ ...lot, venue: venueOnRow(lot) });
      if (!out.has(key)) out.set(key, new Set());
      if (lot.deal_id !== null && lot.deal_id !== undefined) out.get(key).add(lot.deal_id);
    }
    return out;
  }, [navData, compareData]);

  const holdings = compareData ? compareHoldings(positions, priorPositions) : positions.map((p) => ({ ...p, held_now: true }));
  const needle = search.trim().toLowerCase();
  const shownHoldings = holdings.filter((p) =>
    (instrument === "all" || p.instrument_type === instrument)
    && (!needle || [p.name, p.ticker, p.deal_name, p.instrument_type, p.currency]
      .some((v) => String(v || "").toLowerCase().includes(needle))));

  const cash = navData?.cash || [];
  const priorLp = new Map((compareData?.per_lp || []).map((lp) => [lp.lp_id, lp]));
  const investors = (navData?.per_lp || []).map((lp) => ({
    ...lp,
    compare_nav_share: priorLp.has(lp.lp_id) ? priorLp.get(lp.lp_id).nav_share : null,
    change_usd: priorLp.has(lp.lp_id) ? delta(lp.nav_share, priorLp.get(lp.lp_id).nav_share) : null,
  }));

  const refreshAll = useCallback(() => {
    nav.reload();
    history.reload();
    if (compareDate) prior.reload();
  }, [nav, history, prior, compareDate]);

  const closeStatement = useCallback(() => setInvestor(null), []);
  const compareLabel = compareDate ? day(compareDate) : "";

  const compareColumns = compareData ? [
    { key: "compare_mv_usd", label: `Value ${compareLabel}`, align: "right", sortable: true,
      render: (v, row) => (row.held_then ? whole(v) : <span className="nvp-muted">Not held</span>) },
    { key: "change_usd", label: "Change", align: "right", sortable: true,
      render: (v) => <span className={toneClass(v)}>{signed(v)}</span> },
  ] : [];

  const positionCsv = [
    { label: "Holding", value: (r) => r.name },
    { label: "Ticker", value: (r) => r.ticker },
    { label: "Deal", value: (r) => r.deal_name },
    { label: "Type", value: (r) => r.instrument_type },
    { label: "Currency", value: (r) => r.currency },
    { label: "Quantity or face", value: (r) => r.quantity },
    { label: "Price", value: (r) => r.last_price },
    { label: "Price date", value: (r) => r.price_date },
    { label: "Cost (local)", value: (r) => r.cost_basis },
    { label: "Value (local)", value: (r) => r.market_value },
    { label: "Value (USD)", value: (r) => r.mv_usd },
    { label: "Unrealised P&L (local)", value: (r) => r.unrealized_pnl },
    { label: "Valuation basis", value: (r) => r.valuation_method },
    ...(compareData ? [
      { label: `Value ${compareDate} (USD)`, value: (r) => r.compare_mv_usd },
      { label: "Change (USD)", value: (r) => r.change_usd },
    ] : []),
  ];

  return (
    <div className="nvp" aria-busy={nav.refreshing}>
      <header className="nvp-header">
        <div className="nvp-title">
          <h1>NAV / Performance</h1>
          <p className="nvp-anchor">
            {navData ? (
              <>
                NAV <b>{whole(summary.nav)}</b> as of {day(navData.as_of_date)} <PastDateNote basis={navData.as_of_basis} />
                {/* No "since <date>" figure here. The month-to-date tile just
                    below measures the month on the ledger, and the comparison
                    measures it on the marked NAV at an approximate past date;
                    side by side in one line they read as two answers to one
                    question. The comparison lives in the breakdown and the
                    tables, where both dates head their columns. */}
              </>
            ) : nav.loading ? "Loading the valuation…" : "No valuation loaded."}
            <span className="nvp-anchor-fresh">
              <FreshnessBadge savedAt={nav.savedAt} refreshing={nav.refreshing} error={nav.error} onRefresh={refreshAll} />
            </span>
          </p>
        </div>
        <div className="nvp-controls no-print">
          <label className="nvp-field">
            <span>Valuation date</span>
            <input
              type="date" required value={asOfDate} max={today()}
              aria-label="Valuation date"
              onChange={(e) => { if (e.target.value) setAsOfDate(e.target.value); }}
            />
          </label>
          <label className="nvp-field">
            <span>Compare with</span>
            <select aria-label="Compare with" value={compareMode} onChange={(e) => setCompareMode(e.target.value)}>
              <option value="month">Previous month end ({day(previousMonthEnd(asOfDate))})</option>
              <option value="year">Previous year end ({day(previousYearEnd(asOfDate))})</option>
              <option value="custom">Another date…</option>
              <option value="none">No comparison</option>
            </select>
          </label>
          {/* Beside the control that chose the past date, the note that a past
              date is approximate. */}
          {compareData && <span className="nvp-compare-note"><PastDateNote basis={compareData.as_of_basis} /></span>}
          {compareMode === "custom" && (
            <label className="nvp-field">
              <span>Comparison date</span>
              <input
                type="date" value={customCompare} max={today()} aria-label="Comparison date"
                onChange={(e) => setCustomCompare(e.target.value)}
              />
            </label>
          )}
          <PdfButton />
        </div>
      </header>

      {nav.error && !navData && (
        <div role="alert" className="nvp-alert">Could not compute NAV for {day(asOfDate)}. {nav.error}</div>
      )}
      {navData && navData.as_of_date !== asOfDate && !nav.refreshing && (
        <p className="nvp-notice" role="status">
          The valuation for {day(asOfDate)} is unavailable; showing {day(navData.as_of_date)}.
        </p>
      )}
      {compareDate && prior.error && !compareData && (
        <p className="nvp-notice" role="status">Could not load the comparison for {compareLabel}. {prior.error}</p>
      )}

      <PerformanceTiles windows={history.data?.windows} loading={history.loading} error={history.error} />

      <div className="nvp-grid">
        <NetAssetsChart
          history={history.data}
          asOfDate={navData?.as_of_date || asOfDate}
          liveNav={navData && navData.as_of_date === history.data?.as_of ? summary.nav : null}
          loading={history.loading}
          error={history.error}
        />
        {navData && (
          <NavBreakdown
            summary={summary}
            compare={compareData}
            asOfDate={navData.as_of_date}
            compareDate={compareDate}
            compareLoading={prior.loading}
            fxRates={navData.fx_rates}
          />
        )}
      </div>

      {navData && (
        <section className="nvp-panel nvp-details">
          <div className="nvp-tabs" role="tablist" aria-label="NAV detail views">
            {[
              ["positions", "Holdings", positions.length],
              ["cash", "Cash", cash.length],
              ["investors", "Investors", investors.length],
              ["methodology", "Methodology"],
            ].map(([key, label, count]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}>
                {label}{count !== undefined ? ` (${count})` : ""}
              </button>
            ))}
          </div>

          {tab === "positions" && (
            <>
              <div className="nvp-toolbar">
                <div>
                  <h2>Holdings</h2>
                  <p>
                    {shownHoldings.length} of {holdings.length} holdings
                    {summary.lot_count ? ` · ${summary.lot_count} lots` : ""} · cost and P&L in each holding’s own currency
                  </p>
                </div>
                <div className="nvp-filters no-print">
                  <input aria-label="Search holdings" placeholder="Search name, ticker or deal…" value={search} onChange={(e) => setSearch(e.target.value)} />
                  <select aria-label="Instrument type" value={instrument} onChange={(e) => setInstrument(e.target.value)}>
                    <option value="all">All instruments</option>
                    {[...new Set(positions.map((p) => p.instrument_type))].filter(Boolean).map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <ExportCsvButton filename={`nav-holdings-${navData.as_of_date}`} columns={positionCsv} rows={shownHoldings} />
                </div>
              </div>
              <FinTable
                columns={[
                  { key: "name", label: "Holding", sortable: true, render: (v, row) => <Holding row={row} dealIds={dealIds} asOfDate={navData.as_of_date} /> },
                  { key: "quantity", label: "Quantity / face", align: "right", sortable: true, render: (v) => quantity(v) },
                  { key: "last_price", label: "Price", align: "right", sortable: true, render: (v, row) => <Price row={row} /> },
                  { key: "mv_usd", label: "Value (US$)", align: "right", sortable: true,
                    render: (v, row) => (
                      <div className="nvp-stack">
                        <span>{whole(v)}</span>
                        {row.currency && row.currency !== "USD" && toNumber(row.market_value) !== null
                          && <small>{money(row.market_value, { currency: row.currency, whole: true })}</small>}
                      </div>
                    ) },
                  // Narrow screens scroll the table sideways, so what a reader
                  // came for — the value, and how it moved — comes first.
                  ...compareColumns,
                  { key: "unrealized_pnl", label: "Unrealised P&L", align: "right", sortable: true,
                    render: (v, row) => <span className={toneClass(v)}>{money(v, { currency: row.currency, whole: true })}</span> },
                  { key: "cost_basis", label: "Cost", align: "right", sortable: true, render: (v, row) => money(v, { currency: row.currency, whole: true }) },
                ]}
                rows={shownHoldings}
                pageSize={25}
                defaultSort={{ key: "mv_usd", direction: "desc" }}
                emptyMessage={positions.length ? "No holdings match your filters." : "No open holdings on this date."}
              />
            </>
          )}

          {tab === "cash" && (
            <>
              <div className="nvp-toolbar">
                <div>
                  <h2>Cash</h2>
                  <p>{cash.length} accounts · {whole(summary.total_cash)} in all</p>
                </div>
                <div className="nvp-filters no-print">
                  <ExportCsvButton
                    filename={`nav-cash-${navData.as_of_date}`}
                    rows={cash}
                    columns={[
                      { label: "Source", value: (r) => r.source },
                      { label: "Account", value: (r) => r.account_name },
                      { label: "Currency", value: (r) => r.currency },
                      { label: "Balance", value: (r) => r.balance },
                      { label: "US$ per unit", value: (r) => r.fx_rate },
                      { label: "Balance (USD)", value: (r) => r.balance_usd },
                      { label: "Balance as of", value: (r) => r.as_of || navData.as_of_date },
                    ]}
                  />
                </div>
              </div>
              <FinTable
                columns={[
                  { key: "account_name", label: "Account", sortable: true,
                    render: (v, row) => (
                      <div className="nvp-stack nvp-left">
                        <span>{v || "—"}</span>
                        {row.as_of && row.as_of !== navData.as_of_date && <small>balance as of {day(row.as_of)}</small>}
                      </div>
                    ) },
                  { key: "source", label: "Held at", sortable: true, render: (v) => (v ? <Badge text={v} variant="gray" /> : "—") },
                  { key: "balance", label: "Balance", align: "right", sortable: true, render: (v, row) => money(v, { currency: row.currency, whole: true }) },
                  { key: "fx_rate", label: "US$ per unit", align: "right", sortable: true,
                    render: (v, row) => (row.currency === "USD" ? "—" : amount(v, 4)) },
                  { key: "balance_usd", label: "Balance (US$)", align: "right", sortable: true, render: whole },
                ]}
                rows={cash}
                pageSize={25}
                defaultSort={{ key: "balance_usd", direction: "desc" }}
                emptyMessage="No cash balances on this date."
              />
            </>
          )}

          {tab === "investors" && (
            <>
              <div className="nvp-toolbar">
                <div>
                  <h2>
                    Investors{" "}
                    <InfoTip wide label="How investors are shown">
                      <p>
                        Every investor lends to Helena Partners Inc under a loan note for a named deal and is repaid out of
                        that deal’s proceeds. Share of NAV divides this page’s NAV by the ownership recorded for each
                        investor on each deal.
                      </p>
                      <p>Select an investor to see their capital account, read from their notes.</p>
                    </InfoTip>
                  </h2>
                  <p>Select an investor for their capital account</p>
                </div>
                <div className="nvp-filters no-print">
                  <ExportCsvButton
                    filename={`nav-investors-${navData.as_of_date}`}
                    rows={investors}
                    columns={[
                      { label: "Investor", value: (r) => r.name },
                      { label: "Ownership %", value: (r) => r.ownership_pct },
                      { label: "Capital on deal records (USD)", value: (r) => r.invested },
                      { label: "Share of NAV (USD)", value: (r) => r.nav_share },
                      ...(compareData ? [
                        { label: `Share of NAV ${compareDate} (USD)`, value: (r) => r.compare_nav_share },
                        { label: "Change (USD)", value: (r) => r.change_usd },
                      ] : []),
                    ]}
                  />
                </div>
              </div>
              <FinTable
                columns={[
                  { key: "name", label: "Investor", sortable: true,
                    render: (v, row) => (
                      <button
                        type="button" className="nvp-investor"
                        onClick={(e) => { e.stopPropagation(); setInvestor(row); }}
                      >
                        {v}<span aria-hidden="true" className="nvp-investor-go">Statement ›</span>
                      </button>
                    ) },
                  { key: "ownership_pct", label: "Ownership", align: "right", sortable: true, render: (v) => pct(v, 2) },
                  { key: "invested", label: "Capital on deal records", align: "right", sortable: true, render: whole },
                  { key: "nav_share", label: "Share of NAV", align: "right", sortable: true, render: whole },
                  ...(compareData ? [
                    { key: "compare_nav_share", label: `Share ${compareLabel}`, align: "right", sortable: true, render: whole },
                    { key: "change_usd", label: "Change", align: "right", sortable: true,
                      render: (v) => <span className={toneClass(v)}>{signed(v)}</span> },
                  ] : []),
                ]}
                rows={investors}
                onRowClick={(row) => setInvestor(row)}
                pageSize={25}
                defaultSort={{ key: "nav_share", direction: "desc" }}
                emptyMessage="No investor ownership is recorded on the deals."
              />
            </>
          )}

          {tab === "methodology" && <Methodology fxRates={navData.fx_rates} />}
        </section>
      )}

      <InvestorStatement investor={investor} asOfDate={navData?.as_of_date || asOfDate} onClose={closeStatement} />
    </div>
  );
}
